import { ChevronRight, Plus, Route } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaTooltip } from "@/shared/components/overlays/ColocaTooltip";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatRail } from "@/shared/utils/railDisplay";

import { useFeeLabels } from "../../hooks/useFeeLabels";
import type { CorridorRouteCell } from "../../types/paymentRoutes.types";
import {
  ROUTE_STATUS_CONSEQUENCE_KEY,
  ROUTE_STATUS_LABEL_KEY,
  ROUTE_STATUS_PILL_VARIANT,
} from "../../utils/routeFormat";

export interface CorridorRoutesTableProps {
  cells: CorridorRouteCell[];
  isLoading: boolean;
  /** Opens the detail panel of a configured corridor. */
  onView: (cell: CorridorRouteCell) => void;
  /** Opens the create dialog pre-scoped to an unrouted corridor. */
  onCreate: (cell: CorridorRouteCell) => void;
}

/**
 * Coverage of every corridor, one row per corridor **and direction**.
 *
 * The rows come from the operational catalog, not from the route list, so a corridor with no route is a
 * visible row rather than a silent absence — that absence is precisely the state that refuses every
 * operation.
 *
 * `ColocaDataTable` is not responsive (it only scrolls horizontally), so below `md` the page renders
 * `CorridorRouteCards` instead of a squeezed table.
 */
export function CorridorRoutesTable({ cells, isLoading, onView, onCreate }: CorridorRoutesTableProps) {
  const { t } = useTranslations("paymentRoutes");
  const labels = useFeeLabels();

  const columns = useMemo<ColocaDataTableColumn<CorridorRouteCell>[]>(
    () => [
      {
        id: "corridor",
        header: t("th_corridor"),
        // `relative` hosts the severity accent bar — the table styles rows, not cells, by class.
        cellClassName: "relative",
        cell: (cell) => (
          <>
            {cell.status === "MISSING" && (
              <span aria-hidden="true" className="absolute inset-y-0 left-0 w-0.5 bg-destructive" />
            )}
            <span className="inline-flex flex-wrap items-center gap-1.5">
              <ColocaBadge variant="default">{cell.country}</ColocaBadge>
              <ColocaBadge variant="secondary" className="font-sans">
                {formatRail(cell.rail)}
              </ColocaBadge>
              <span className="font-mono text-xs text-muted-foreground">{cell.currency}</span>
              {cell.offCatalog && (
                <ColocaBadge variant="secondary" className="border-dashed font-sans text-muted-foreground">
                  {t("offcatalog_badge")}
                </ColocaBadge>
              )}
            </span>
          </>
        ),
      },
      {
        id: "direction",
        header: t("th_direction"),
        cell: (cell) => (
          <ColocaBadge variant="default" className="font-sans">
            {labels.direction[cell.direction]}
          </ColocaBadge>
        ),
      },
      {
        id: "status",
        header: t("th_status"),
        cell: (cell) => (
          <ColocaTooltip
            triggerRender={
              <span className="cursor-help">
                <ColocaStatusPill variant={ROUTE_STATUS_PILL_VARIANT[cell.status]} size="sm">
                  {t(ROUTE_STATUS_LABEL_KEY[cell.status])}
                </ColocaStatusPill>
              </span>
            }
          >
            {t(ROUTE_STATUS_CONSEQUENCE_KEY[cell.status])}
          </ColocaTooltip>
        ),
      },
      {
        id: "model",
        header: t("th_model"),
        cell: (cell) =>
          cell.route ? (
            <ColocaTooltip
              triggerRender={
                <span className="cursor-help text-xs text-muted-foreground underline decoration-dotted underline-offset-2">
                  {labels.model[cell.route.economic_model]}
                </span>
              }
            >
              {labels.modelHelp[cell.route.economic_model]}
            </ColocaTooltip>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        id: "provider",
        header: t("th_provider"),
        cell: (cell) => {
          if (!cell.route) return <span className="text-muted-foreground">{t("provider_empty")}</span>;
          if (!cell.providerDeclaresCorridor) {
            return <span className="text-xs text-warning">{t("provider_unknown")}</span>;
          }
          return (
            <span className="text-xs text-muted-foreground">
              {t("provider_corridors", { count: cell.providerCorridorCount })}
            </span>
          );
        },
      },
      {
        id: "action",
        header: "",
        headerClassName: "text-right",
        cellClassName: "text-right",
        cell: (cell) =>
          cell.route ? (
            <ColocaButton variant="ghost" size="xs" iconTrailing={ChevronRight} onClick={() => onView(cell)}>
              {t("action_view")}
            </ColocaButton>
          ) : (
            <ColocaButton
              variant="ghost"
              size="xs"
              icon={Plus}
              className="text-warning"
              onClick={() => onCreate(cell)}
            >
              {t("action_create")}
            </ColocaButton>
          ),
      },
    ],
    [t, labels, onView, onCreate],
  );

  return (
    <ColocaDataTable
      columns={columns}
      rows={cells}
      rowKey={(cell) => cell.key}
      loading={isLoading}
      skeletonRows={8}
      minWidth={780}
      rowClassName={(cell) => cn(cell.offCatalog && "bg-muted/40")}
      empty={
        <ColocaEmpty
          icon={Route}
          title={t("empty_title")}
          description={t("empty_description")}
          surface="card"
        />
      }
    />
  );
}
